"use client"

import type React from "react"
import { useEffect, useState, useRef } from "react"
import axios from "axios"
import { ShieldCheck, ShieldAlert, UserPlus, AlertTriangle } from "lucide-react"
import { useAuthStore } from "../store/authStore"
import { useChatStore, isInContacts } from "../store/chatStore"
import { hashMessage, encryptMessage, signMessage, decryptMessage } from "../crypto/crypto"
import { env } from "../config/env"
import { toast } from "sonner"

interface ChatWindowProps {
  onBack?: () => void
}

const ChatWindow: React.FC<ChatWindowProps> = ({ onBack }) => {
  const API_URL = env.API_URL
  const WS_URL = env.WS_URL
  const { username, token, privateKey, publicKeyX, publicKeyY } = useAuthStore()
  const { activeChat, messages, addMessage, refreshContacts } = useChatStore()
  const [messageInput, setMessageInput] = useState("")
  const [loading, setLoading] = useState(false)
  const [addingContact, setAddingContact] = useState(false)
  const [activeChatInContacts, setActiveChatInContacts] = useState(true)
  const [activeChatName, setActiveChatName] = useState("")
  const wsRef = useRef<WebSocket | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Initialize WebSocket
  useEffect(() => {
    if (!token || !username) return

    const ws = new WebSocket(WS_URL)

    ws.onopen = () => {
      console.log("WebSocket connected")
      // Authenticate
      ws.send(JSON.stringify({ type: "auth", token }))
    }

    ws.onmessage = (event) => {
      const message = JSON.parse(event.data)
      console.log("WebSocket message:", message)

      if (message.type === "chat" && message.data) {
        const data = message.data

        // Check if sender is in contacts
        const senderInContacts = isInContacts(data.sender_username)

        // Decrypt message
        decryptMessage(data.encrypted_message, data.ephemeral_public_key, data.iv, data.auth_tag, privateKey!)
          .then((decrypted) => {
            addMessage({
              id: Date.now().toString(),
              sender: data.sender_username,
              receiver: data.receiver_username,
              content: decrypted,
              encrypted_content: data.encrypted_message,
              timestamp: data.timestamp,
              signature_valid: data.signature_valid && senderInContacts,
              isRead: false,
              senderInContacts,
            })
          })
          .catch((error) => {
            console.error("Failed to decrypt message:", error)
            // Show encrypted message even when decryption fails
            addMessage({
              id: Date.now().toString(),
              sender: data.sender_username,
              receiver: data.receiver_username,
              content: data.encrypted_message,
              encrypted_content: data.encrypted_message,
              timestamp: data.timestamp,
              signature_valid: false,
              isRead: false,
              senderInContacts,
            })
            toast.warning("Message decryption failed", {
              description: `Unable to decrypt message from ${data.sender_username}`,
            })
          })
      }
    }

    ws.onerror = (error) => {
      console.error("WebSocket error:", error)
      toast.error("Connection error", {
        description: "Failed to establish real-time messaging connection",
      })
    }

    wsRef.current = ws

    return () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.close()
      }
    }
  }, [token, username, privateKey, addMessage, WS_URL])

  // Check if active chat is in contacts and fetch name
  useEffect(() => {
    const checkAndFetchContacts = async () => {
      if (!activeChat || !token) return

      try {
        const userResponse = await axios.get(`${API_URL}/users/${activeChat}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        setActiveChatName(userResponse.data.data.name || activeChat)

        // Fetch contact list
        const response = await axios.get(`${API_URL}/users/contacts/list`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        const contactUsernames = response.data.data.map((c: { contactUsername: string }) => c.contactUsername)
        refreshContacts(contactUsernames)
        setActiveChatInContacts(contactUsernames.includes(activeChat))
      } catch (error) {
        console.error("Failed to fetch contacts:", error)
        toast.error("Failed to fetch contact information", {
          description: "Could not load contact details",
        })
        setActiveChatName(activeChat)
      }
    }

    checkAndFetchContacts()
  }, [activeChat, token, API_URL, refreshContacts])

  // Fetch messages when active chat changes
  useEffect(() => {
    const fetchMessages = async () => {
      if (!activeChat || !token || !privateKey) return

      try {
        const response = await axios.get(`${API_URL}/messages/${activeChat}`, {
          headers: { Authorization: `Bearer ${token}` },
        })

        const fetchedMessages = response.data.data?.messages || []

        // Decrypt and add messages
        for (const msg of fetchedMessages) {
          try {
            let publicKeyForDecryption = msg.ephemeral_public_key

            if (msg.sender_username === username) {
              const recipientResponse = await axios.get(`${API_URL}/users/${msg.receiver_username}`, {
                headers: { Authorization: `Bearer ${token}` },
              })
              publicKeyForDecryption = `${recipientResponse.data.data.publicKeyX}:${recipientResponse.data.data.publicKeyY}`
            }

            const decrypted = await decryptMessage(
              msg.encrypted_message,
              publicKeyForDecryption,
              msg.iv,
              msg.auth_tag,
              privateKey,
            )

            const senderInContacts = isInContacts(msg.sender_username)
            addMessage({
              id: msg.id,
              sender: msg.sender_username,
              receiver: msg.receiver_username,
              content: decrypted,
              encrypted_content: msg.encrypted_message,
              timestamp: msg.timestamp,
              signature_valid: msg.signature_valid && senderInContacts,
              isRead: msg.is_read,
              senderInContacts,
            })
          } catch (error) {
            console.error("Failed to decrypt message:", error)
            // Show encrypted message when decryption fails
            const senderInContacts = isInContacts(msg.sender_username)
            // Show encrypted message even when decryption fails
            addMessage({
              id: Date.now().toString(),
              sender: msg.sender_username,
              receiver: msg.receiver_username,
              content: msg.encrypted_message,
              encrypted_content: msg.encrypted_message,
              timestamp: msg.timestamp,
              signature_valid: false,
              isRead: false,
              senderInContacts,
            })
            toast.warning("Message decryption failed", {
              description: `Unable to decrypt message from ${msg.sender_username}`,
            })
          }
        }
      } catch (error) {
        console.error("Failed to fetch messages:", error)
        toast.error("Failed to load messages", {
          description: `Could not fetch chat history with ${activeChat}`,
        })
      }
    }

    fetchMessages()
  }, [activeChat, token, privateKey, API_URL, addMessage, username])

  const handleAddContact = async () => {
    if (!activeChat) return

    setAddingContact(true)
    try {
      await axios.post(
        `${API_URL}/users/contacts/add`,
        { contactUsername: activeChat },
        { headers: { Authorization: `Bearer ${token}` } },
      )

      // Refresh contact list
      const response = await axios.get(`${API_URL}/users/contacts/list`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const contactUsernames = response.data.data.map((c: { contactUsername: string }) => c.contactUsername)
      refreshContacts(contactUsernames)
      setActiveChatInContacts(true)

      toast.success("Contact added successfully!", {
        description: `${activeChat} has been added to your contacts`,
      })
    } catch (error) {
      toast.error("Failed to add contact", {
        description: `Could not add ${activeChat} to your contacts`,
      })
      console.error("Failed to add contact:", error)
    } finally {
      setAddingContact(false)
    }
  }

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages])

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!messageInput.trim() || !activeChat || !username) return

    setLoading(true)

    try {
      // Get recipient's public key
      const recipientResponse = await axios.get(`${API_URL}/users/${activeChat}`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      const recipientPublicKeyX = recipientResponse.data.data.publicKeyX
      const recipientPublicKeyY = recipientResponse.data.data.publicKeyY

      // Hash message
      const timestamp = new Date().toISOString()
      const messageHash = hashMessage(messageInput, username, activeChat, timestamp)

      // Sign message
      const signature = signMessage(messageHash, privateKey!)

      // Encrypt message
      console.log(`Input: ${messageInput}, rec x: ${recipientPublicKeyX}, rec y: ${recipientPublicKeyY}`)
      const encrypted = await encryptMessage(
        messageInput,
        recipientPublicKeyX,
        recipientPublicKeyY,
        privateKey!,
        publicKeyX!,
        publicKeyY!,
      )

      // Send message
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(
          JSON.stringify({
            type: "chat",
            sender_username: username,
            receiver_username: activeChat,
            encrypted_message: encrypted.ciphertext,
            ephemeral_public_key: encrypted.ephemeralPublicKey,
            iv: encrypted.iv,
            auth_tag: encrypted.authTag,
            message_hash: messageHash,
            signature,
            timestamp,
          }),
        )

        addMessage({
          id: Date.now().toString(),
          sender: username,
          receiver: activeChat,
          content: messageInput,
          encrypted_content: encrypted.ciphertext,
          timestamp,
          signature_valid: true,
          isRead: true,
        })

        setMessageInput("")
      }
    } catch (error) {
      toast.warning("Failed to send message", {
        description: `Failed to send message to ${activeChat}`,
      })
      console.error("Failed to send message:", error)
    } finally {
      setLoading(false)
    }
  }

  const filteredMessages = messages.filter(
    (msg) =>
      (msg.sender === username && msg.receiver === activeChat) ||
      (msg.sender === activeChat && msg.receiver === username),
  )

  return (
    <div className="h-full bg-slate-800 rounded-lg border border-slate-700 flex flex-col">
      {/* Chat Header */}
      <div className="p-3 sm:p-4 border-b border-slate-700 flex items-center gap-3">
        {onBack && (
          <button onClick={onBack} className="lg:hidden text-white hover:bg-slate-700 p-2 rounded -ml-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="text-base sm:text-lg font-semibold text-white truncate">{activeChatName}</h3>
          <p className="text-xs text-slate-400 truncate">@{activeChat}</p>
        </div>
      </div>

      {/* Non-Contact Warning */}
      {!activeChatInContacts && (
        <div className="bg-yellow-500/20 border-b border-yellow-500/50 px-3 sm:px-4 py-2 sm:py-3 flex items-start gap-2 sm:gap-3">
          <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-yellow-200 text-xs sm:text-sm font-medium mb-1">Not in contacts</p>
            <p className="text-yellow-300/80 text-xs mb-2 hidden sm:block">
              Messages from non-contacts cannot be verified. Add them to your contacts to enable verification.
            </p>
            <button
              onClick={handleAddContact}
              disabled={addingContact}
              className="inline-flex items-center gap-1.5 sm:gap-2 bg-yellow-600 hover:bg-yellow-700 disabled:bg-yellow-800 text-white text-xs px-2.5 sm:px-3 py-1 sm:py-1.5 rounded transition cursor-pointer"
            >
              <UserPlus className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              {addingContact ? "Adding..." : "Add to Contacts"}
            </button>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-2 sm:space-y-3">
        {filteredMessages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.sender === username ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[85%] sm:max-w-xs md:max-w-md px-3 sm:px-4 py-2 rounded-lg ${
                msg.sender === username ? "bg-blue-600 text-white" : "bg-slate-700 text-slate-100"
              }`}
            >
              {msg.sender !== username && msg.senderInContacts === false && (
                <div className="text-xs text-yellow-300 mb-1 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  <span>Not in contacts</span>
                </div>
              )}
              <p className="text-sm sm:text-base overflow-wrap-anywhere">{msg.content}</p>
              <div className="text-xs opacity-70 mt-1 flex items-center gap-1">
                {msg.signature_valid ? (
                  <>
                    <ShieldCheck className="w-3 h-3" />
                    <span>Verified</span>
                  </>
                ) : (
                  <>
                    <ShieldAlert className="w-3 h-3" />
                    <span>Unverified</span>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <form onSubmit={handleSendMessage} className="p-3 sm:p-4 border-t border-slate-700 flex gap-2">
        <input
          type="text"
          value={messageInput}
          onChange={(e) => setMessageInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault()
              handleSendMessage(e as unknown as React.FormEvent)
            }
          }}
          placeholder="Type a message..."
          disabled={loading}
          className="flex-1 px-3 sm:px-4 py-2 text-sm sm:text-base bg-slate-700 border border-slate-600 rounded text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
        />
        <button
          type="submit"
          disabled={loading || !messageInput.trim()}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 text-white px-6 py-2 rounded transition cursor-pointer"
        >
          {loading ? "Sending..." : "Send"}
        </button>
      </form>
    </div>
  )
}

export default ChatWindow
